import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  CopyClipboard,
  DisplayText,
  Heading,
  Link,
  Modal,
  TextStyle
} from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import {
  COMMON_CONSTANTS,
  DateTimeFormatter,
  getDateTimeFormat,
  Loader
} from '@nile/components';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { TicketClient } from '@nile/graphql';
import { GET_CONVERSATION } from '@nile/components/src/Pages/SystemTrayChatPage/graphql';
import ChatBubble from '@nile/components/src/Pages/SystemTrayChatPage/subpages/ChatBubble';
import {
  EVENT,
  SOURCE
} from '@nile/components/src/Pages/SystemTrayChatPage/constants';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';

const THREAD_ACTIVITY_TYPE = {
  INITIATED: 'INITIATED',
  CLOSED: 'CLOSED'
};

function ChatUpdateBubble(props) {
  const { data } = props;
  const [openModal, setOpenModal] = useState(false);

  const type = data?.conversationThread?.status;
  const intiator = data?.conversationThread?.recipients?.initiator?.user?.name;
  const reciever = data?.conversationThread?.recipients?.receiver?.[0]?.name;

  // Similar styling structure to ConversationBubbleNew
  const bubbleWrapperClassNames = ['flex center g10 bubble-wrapper', 'px10']
    .filter(Boolean)
    .join(' ');

  const bubbleOuterClassNames = ['w100'].filter(Boolean).join(' ');

  const bubblePadding = 'pt20 pb20 px10';

  const bubbleClassNames = ['word-break br20', bubblePadding]
    .filter(Boolean)
    .join(' ');

  const headerClassNames = ['bubble-header fsize12 flex actr fx-sb fg1']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={bubbleWrapperClassNames}>
      <div id="conversation-bubble-outer" className={bubbleOuterClassNames}>
        <Card variant="card" padding="sizeExtraSmall" border="none">
          <div id="conversation-bubble-inner" className={bubbleClassNames}>
            <div className="flex g10 align-items-center">
              <div className="mr8">
                <Avatar
                  size={'extraSmall'}
                  source={`${envConfig.imageRoot}svg/notification.svg`}
                />
              </div>
              <div className={headerClassNames}>
                <div className="added-by flex g5 actr">
                  <div className="flex g3">
                    <div className="flex actr f11 g10">
                      <FormattedMessage
                        id={
                          type === THREAD_ACTIVITY_TYPE.INITIATED
                            ? 'msp.chat.updates.initiatedBy'
                            : 'msp.chat.updates.closedBy'
                        }
                      />
                      <Avatar initials={intiator?.[0]} size="extraSmall" />
                      <TextStyle weight="SemiBold">
                        {data?.user?.name || (
                          <FormattedMessage id="reports.assetActivity.pdf.system" />
                        )}
                      </TextStyle>
                      {type === THREAD_ACTIVITY_TYPE.INITIATED ? (
                        <>
                          <FormattedMessage id="common.label.for" />
                          <TextStyle weight="SemiBold">{reciever}</TextStyle>
                        </>
                      ) : (
                        <></>
                      )}
                      {type === THREAD_ACTIVITY_TYPE.CLOSED ? (
                        <div className="flex">
                          <Link
                            onClick={() =>
                              setOpenModal((prevState) => !prevState)
                            }
                          >
                            <FormattedMessage id="common.labal.chatId" />
                            <TextStyle weight="SemiBold">
                              {` #${data?.conversationThread?.displayId}`}
                            </TextStyle>
                          </Link>
                          {openModal && (
                            <ConversationModal
                              openModal={openModal}
                              setOpenModal={setOpenModal}
                              data={data}
                            />
                          )}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex g3 right-container actr">
                  <TextStyle
                    variation="subdued"
                    title={
                      <DateTimeFormatter type="dateTime" date={data?.time} />
                    }
                  >
                    <div className="flex">
                      <DateTimeFormatter type="dateTime" date={data?.time} />
                    </div>
                  </TextStyle>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

const ConversationModal = ({ openModal, data, setOpenModal }) => {
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState([]);

  const [copied, setCopyState] = useState(false);
  const onCopy = () => {
    setCopyState(!copied);
    setTimeout(() => {
      setCopyState(false);
    }, 1000);
  };

  const getConversations = () => {
    TicketClient.query({
      query: GET_CONVERSATION,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        input: {
          conversationThread: {
            threadId: data?.conversationThread?.threadId
          },
          listInfo: {
            pageSize: 500,
            page: 1,
            sort: [
              {
                attribute: 'time',
                order: 'ASC'
              }
            ],
            condition: null
          }
        }
      }
    })
      .then((res) => {
        const modifiedRes = res?.data?.getConversations?.conversations?.map(
          (el) => ({
            ...el,
            isHttp: true
          })
        );
        setConversation(modifiedRes);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getContent = (cont) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(cont?.content, 'text/html');
    const plainText = doc.body.innerText || doc.body.textContent || '';

    const name =
      cont?.type === EVENT.TECH_REPLY
        ? data?.conversationThread?.recipients?.initiator?.user?.name
        : data?.conversationThread?.recipients?.receiver?.[0]?.name;

    const content = plainText;
    const { MSPDateFormat, MSPtimeFormat } = getDateTimeFormat();
    const time = dayjs(cont?.time).format(`${MSPDateFormat} ${MSPtimeFormat}`);

    return `${name}:\n${content}\n${time}`;
  };

  useEffect(() => {
    if (openModal) {
      getConversations();
    }
  }, [openModal]);

  return (
    <>
      <Modal
        open={openModal}
        medium
        showCloseButton={false}
        title={
          <div className="flex">
            <div className="flex f11 g20">
              <div className="flex-center">
                <Avatar
                  size="small"
                  initials={
                    data?.conversationThread?.recipients?.receiver?.[0]
                      ?.name?.[0]
                  }
                />
              </div>
              <div className="flex f11 fcol">
                <div className="flex actr g10">
                  <Heading element="h4">
                    <TextStyle weight="SemiBold">
                      {
                        data?.conversationThread?.recipients?.receiver?.[0]
                          ?.name
                      }
                    </TextStyle>
                  </Heading>
                  {!loading && (
                    <CopyClipboard
                      text={conversation
                        ?.filter((con) => con?.content)
                        ?.map((el) => getContent(el))
                        .join('\n')}
                      onCopy={onCopy}
                    >
                      <Button
                        plain
                        icon={<SVGIcon name="TASK_IDENTIFIER" size="xs" />}
                      >
                        <div className="flex g10 center">
                          <DisplayText>
                            <FormattedMessage id="common.label.copy.transcript" />
                          </DisplayText>
                          <div
                            className={`ml-10 copytag-transition browser-firefox p-2-6 ${
                              copied ? 'op-1' : 'op-0'
                            }`}
                            visible={copied}
                          >
                            <DisplayText>
                              <FormattedMessage id="admin.customerSupportAccess.copied" />
                            </DisplayText>
                          </div>
                        </div>
                      </Button>
                    </CopyClipboard>
                  )}
                </div>
                <div className="flex g10">
                  <DisplayText>
                    <TextStyle variation="subdued">
                      <Link>
                        {data?.conversationThread?.asset?.name ||
                          data?.conversationThread?.recipients?.receiver?.[0]
                            ?.name}
                      </Link>
                    </TextStyle>
                  </DisplayText>
                  <DisplayText>
                    <FormattedMessage id="msp.chat.updates.initiatedOn" />
                  </DisplayText>
                  <DisplayText>
                    <TextStyle variation="subdued">
                      <div className="flex">
                        <DateTimeFormatter
                          type="dateTime"
                          date={conversation?.[0]?.time}
                        />
                      </div>
                    </TextStyle>
                  </DisplayText>
                </div>
              </div>
            </div>
            <div
              className="flex ptr"
              onClick={() => setOpenModal((prevState) => !prevState)}
            >
              <SVGIcon name="Close" />
            </div>
          </div>
        }
      >
        {loading ? (
          <div className="flex-center height-70vh max-height-1000">
            <Loader />
          </div>
        ) : (
          <div className="height-70vh max-height-1000 p20 bg-lvmist scroll">
            {isEmpty(conversation?.filter((el) => el?.type !== EVENT.EVENT)) ? (
              <div className="flex-center fcol full-width">
                <SVGIcon name="EmptyBox" />
                <FormattedMessage id="settings.ai.page.prompt.conversations.results.empty" />
              </div>
            ) : (
              <div>
                {conversation?.map((convo, index) => (
                  <ChatBubble
                    source={SOURCE.WEB}
                    key={`conv-${index}`}
                    data={convo}
                    threadData={data?.conversationThread}
                    isCompact
                    readOnly
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default ChatUpdateBubble;
